import React, { useEffect, useRef, useMemo } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { ChequePaymentData } from '../../collectPayment.types';
import useCurrency from '../../../../../components/CurrencyProvider';

interface ChequePaymentFormProps {
  data: ChequePaymentData;
  onChange: (data: ChequePaymentData) => void;
  errors?: {
    amount?: string;
    chequeNumber?: string;
    date?: string;
    bankName?: string;
  };
  remainingAmount?: number;
}

const ChequePaymentForm: React.FC<ChequePaymentFormProps> = ({
  data,
  onChange,
  errors,
  remainingAmount,
}) => {
  const { currencySymbol } = useCurrency();
  const prevRemainingRef = useRef(remainingAmount);
  const onChangeRef = useRef(onChange);

  // Keep refs updated with latest values
  onChangeRef.current = onChange;

  // Auto-populate amount with remaining amount when:
  // 1. Form is first rendered with no amount, OR
  // 2. Remaining amount changes (after adding a payment) and current amount is 0
  useEffect(() => {
    const remainingChanged = prevRemainingRef.current !== remainingAmount;
    prevRemainingRef.current = remainingAmount;
    
    if (remainingAmount && remainingAmount > 0 && !data.amount) {
      onChangeRef.current({ ...data, amount: remainingAmount });
    } else if (remainingChanged && remainingAmount && remainingAmount > 0 && data.amount === 0) {
      onChangeRef.current({ ...data, amount: remainingAmount });
    }
  }, [remainingAmount, data]);

  // Real-time validation: check if amount exceeds remaining
  const amountError = useMemo(() => {
    if (errors?.amount) return errors.amount;
    if (remainingAmount !== undefined && data.amount > remainingAmount) {
      return 'Amount cannot exceed remaining balance';
    }
    return undefined;
  }, [errors?.amount, data.amount, remainingAmount]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Amount"
          type="number"
          value={data.amount || ''}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            onChange({ ...data, amount: value });
          }}
          placeholder="0.00"
          error={!!amountError}
          helperText={amountError}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Cheque Number"
          value={data.chequeNumber || ''}
          onChange={(e) => onChange({ ...data, chequeNumber: e.target.value })}
          placeholder="Enter Cheque Number"
          error={!!errors?.chequeNumber}
          helperText={errors?.chequeNumber}
        />
      </Box>

      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Date"
            type="date"
            value={data.date || ''}
            onChange={(e) => onChange({ ...data, date: e.target.value })}
            error={!!errors?.date}
            helperText={errors?.date}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Bank Name"
            value={data.bankName || ''}
            onChange={(e) => onChange({ ...data, bankName: e.target.value })}
            placeholder="Enter Bank Name"
            error={!!errors?.bankName}
            helperText={errors?.bankName}
          />
        </Grid2>
      </Grid2>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Notes"
          multiline
          minRows={2}
          maxRows={4}
          value={data.notes || ''}
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          placeholder="Enter Note"
        />
      </Box>
    </Box>
  );
};

export default ChequePaymentForm;
